import React, { useState, useRef, useEffect } from 'react';
import {
  Box, TextField, Button, Typography, CircularProgress,
  Avatar, Chip, LinearProgress, Collapse,
} from '@mui/material';
import { Send, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import api from '../services/api';

// ── Markdown renderer simples (negrito, bullet, quebras) ──────────────────────
function FormattedText({ text }) {
  if (!text) return null;

  const lines = text.split('\n');
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
      {lines.map((line, i) => {
        if (!line.trim()) return <Box key={i} sx={{ height: 4 }} />;

        const renderInline = (str) => {
          const parts = str.split(/(\*\*[^*]+\*\*)/g);
          return parts.map((part, j) =>
            part.startsWith('**') && part.endsWith('**')
              ? <strong key={j}>{part.slice(2, -2)}</strong>
              : part
          );
        };

        const isBullet = /^[•\-]\s/.test(line);
        const isNumbered = /^\d+\.\s/.test(line);

        if (isBullet || isNumbered) {
          return (
            <Box key={i} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
              <Typography sx={{ fontSize: 'inherit', lineHeight: 1.6, flexShrink: 0, opacity: 0.5 }}>
                {isBullet ? '•' : line.match(/^\d+\./)[0]}
              </Typography>
              <Typography sx={{ fontSize: 'inherit', lineHeight: 1.6 }}>
                {renderInline(line.replace(/^[•\-]\s/, '').replace(/^\d+\.\s/, ''))}
              </Typography>
            </Box>
          );
        }

        return (
          <Typography key={i} sx={{ fontSize: 'inherit', lineHeight: 1.6 }}>
            {renderInline(line)}
          </Typography>
        );
      })}
    </Box>
  );
}

// ── Sugestões rápidas ─────────────────────────────────────────────────────────
const SUGGESTIONS = [
  'Quanto vendeu hoje?',
  'Quem mais vendeu esta semana?',
  'Quantas funcionárias trabalharam hoje?',
  'Vendas por hotel hoje',
  'Top 5 funcionárias do mês',
];

const formatTime = (ts) => {
  const d = new Date(ts);
  const diffH = Math.abs(new Date() - d) / 36e5;
  return diffH < 24
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString([], { day: '2-digit', month: '2-digit' });
};

// ── Linha de status (consultando / corrigindo) ─────────────────────────────────
function StatusRow({ text }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
      <CircularProgress size={14} sx={{ color: '#007AFF' }} />
      <Typography sx={{ fontSize: '0.85rem', color: '#86868b' }}>{text}</Typography>
    </Box>
  );
}

// ── Um item de pergunta/resposta ────────────────────────────────────────────────
function QAEntry({ entry, open, onToggle, expandedSQL, expandedErr, onToggleSQL, onToggleErr }) {
  const statusDotColor = entry.status !== 'done'
    ? '#007AFF'
    : entry.isSuccess === false ? '#FF3B30' : '#34C759';

  if (!open) {
    return (
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25,
          cursor: 'pointer', borderBottom: '1px solid rgba(0,0,0,0.06)',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
        }}
      >
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: statusDotColor, flexShrink: 0 }} />
        <Typography sx={{
          flexGrow: 1, fontSize: '0.9rem', fontWeight: 500, color: '#1d1d1f',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {entry.question}
        </Typography>
        <Typography sx={{ fontSize: '0.72rem', color: '#86868b', flexShrink: 0 }}>
          {entry.employeeName ? `${entry.employeeName} · ` : ''}{formatTime(entry.timestamp)}
        </Typography>
        <ChevronDown size={16} color="#86868b" />
      </Box>
    );
  }

  return (
    <Box sx={{
      borderRadius: 3, bgcolor: 'rgba(255,255,255,0.95)',
      border: '1px solid rgba(0,0,0,0.08)', boxShadow: '0 2px 14px rgba(0,0,0,0.05)',
      overflow: 'hidden', mb: 1.5,
    }}>
      <Box
        onClick={onToggle}
        sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, p: 2, cursor: 'pointer' }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{
            fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em',
            color: '#007AFF', textTransform: 'uppercase', mb: 0.4,
          }}>
            Pergunta
          </Typography>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 600, color: '#1d1d1f', lineHeight: 1.35 }}>
            {entry.question}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, mt: 0.3 }}>
          <Typography sx={{ fontSize: '0.72rem', color: '#86868b' }}>
            {entry.employeeName ? `${entry.employeeName} · ` : ''}{formatTime(entry.timestamp)}
          </Typography>
          <ChevronUp size={16} color="#86868b" />
        </Box>
      </Box>

      <Box sx={{ px: 2, pb: 2, borderTop: '1px solid rgba(0,0,0,0.06)', pt: 1.5 }}>
        {entry.status === 'pending' && <StatusRow text="Consultando o banco..." />}
        {entry.status === 'retrying' && (
          <>
            <StatusRow text={`Ajustando a consulta (tentativa ${entry.attempt}/${entry.totalAttempts})...`} />
            <LinearProgress variant="indeterminate" sx={{
              height: 2, mt: 0.5, borderRadius: 1, bgcolor: 'rgba(0,122,255,0.1)',
              '& .MuiLinearProgress-bar': { bgcolor: '#007AFF' },
            }} />
          </>
        )}

        {entry.status === 'done' && (
          <Box sx={{
            fontSize: '0.95rem',
            color: entry.isConnError ? '#FF3B30' : '#1d1d1f',
          }}>
            <FormattedText text={entry.answer} />

            {entry.sqlQuery && (
              <Box sx={{ mt: 1.25 }}>
                <Button
                  size="small"
                  onClick={() => onToggleSQL(entry.id)}
                  endIcon={expandedSQL ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                  sx={{ fontSize: '0.68rem', p: '2px 6px', minWidth: 0, color: '#86868b', textTransform: 'none', fontWeight: 500 }}
                >
                  SQL
                </Button>
                <Collapse in={expandedSQL}>
                  <Box sx={{ mt: 0.5, p: 1, borderRadius: 1.5, bgcolor: 'rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.08)' }}>
                    <Typography variant="caption" sx={{
                      fontFamily: 'SF Mono, Monaco, monospace', fontSize: '0.68rem', lineHeight: 1.5,
                      color: '#444', whiteSpace: 'pre-wrap', display: 'block',
                    }}>
                      {entry.sqlQuery}
                    </Typography>
                  </Box>
                </Collapse>
              </Box>
            )}

            {entry.finalError && (
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  onClick={() => onToggleErr(entry.id)}
                  startIcon={<AlertCircle size={12} />}
                  endIcon={expandedErr ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  sx={{ fontSize: '0.68rem', color: '#FF3B30', p: '2px 6px', minWidth: 0, textTransform: 'none' }}
                >
                  Ver erro
                </Button>
                <Collapse in={expandedErr}>
                  <Typography variant="caption" sx={{
                    display: 'block', color: '#FF3B30', bgcolor: 'rgba(255,59,48,0.08)',
                    p: 1, borderRadius: 1, mt: 0.5, fontFamily: 'SF Mono, Monaco, monospace',
                    fontSize: '0.68rem', lineHeight: 1.4,
                  }}>
                    {entry.finalError}
                  </Typography>
                </Collapse>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
const MiniBotPage = () => {
  const [history, setHistory]         = useState([]);
  const [input, setInput]             = useState('');
  const [loading, setLoading]         = useState(false);
  const [openIds, setOpenIds]         = useState(() => new Set());
  const [expandedSQL, setExpandedSQL] = useState({});
  const [expandedErr, setExpandedErr] = useState({});
  const [loadingHistory, setLoadingHistory] = useState(true);

  const inputRef = useRef(null);

  // O histórico é salvo no backend (quem perguntou, quando) — recarrega aqui
  // pra sobreviver a um F5, já que antes ele só vivia no estado do componente.
  useEffect(() => {
    let cancelled = false;
    api.get('/chat/history?limit=100')
      .then(res => {
        if (cancelled) return;
        const rows = (res.data.history || []).map(row => ({
          id: row.id,
          question: row.question,
          employeeName: row.employee_name,
          timestamp: row.created_at,
          status: 'done',
          answer: row.answer,
          sqlQuery: row.sql_query,
          isSuccess: row.success,
          finalError: row.error,
        }));
        // Preserva qualquer pergunta feita antes desse fetch terminar
        // (ex: usuário digitou e enviou assim que a página abriu).
        setHistory(prev => [
          ...prev.filter(e => !rows.some(r => r.id === e.id)),
          ...rows,
        ]);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingHistory(false); });
    return () => { cancelled = true; };
  }, []);

  const toggleSQL = (id) => setExpandedSQL(p => ({ ...p, [id]: !p[id] }));
  const toggleErr = (id) => setExpandedErr(p => ({ ...p, [id]: !p[id] }));
  const toggleOpen = (id) => setOpenIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const patchEntry = (id, patch) =>
    setHistory(prev => prev.map(e => (e.id === id ? { ...e, ...patch } : e)));

  // Cada pergunta é resolvida sozinha pelo backend (sem memória de conversa),
  // então cada `ask` cria/atualiza UMA entrada — não uma troca de mensagens.
  const ask = async (queryText = null, isRetry = false, previousError = null, attempt = 1, entryId = null) => {
    const text = queryText || input.trim();
    if (!text) return;

    let id = entryId;
    if (!isRetry) {
      id = Date.now();
      setHistory(prev => [{ id, question: text, timestamp: new Date().toISOString(), status: 'pending' }, ...prev]);
      setOpenIds(new Set([id]));
      setInput('');
    }

    setLoading(true);

    try {
      const payload = { query: text };
      if (previousError) payload.previous_error = previousError;

      const response = await api.post('/chat/query', payload);

      if (response.data.needs_retry && attempt <= 3) {
        patchEntry(id, { status: 'retrying', attempt, totalAttempts: 3 });
        setTimeout(() => ask(text, true, response.data.error, attempt + 1, id), 1000);
        return;
      }

      const isSuccess = !response.data.error;
      const finalAnswer = response.data.error
        ? 'Não consegui processar após 3 tentativas.'
        : response.data.response;

      patchEntry(id, {
        status: 'done',
        answer: finalAnswer,
        sqlQuery: response.data.sql_query,
        isSuccess,
        finalError: response.data.error,
      });

      // Registra a pergunta no histórico só aqui, no estado final visto pelo
      // usuário — o backend não sabe se uma falha ainda vai ser tentada de novo.
      api.post('/chat/log', {
        question: text, answer: finalAnswer, sql_query: response.data.sql_query,
        success: isSuccess, error: response.data.error,
      }).catch(() => {});

      if (attempt > 1 && previousError && response.data.sql_query && !response.data.error) {
        await api.post('/chat/learn', { error_key: previousError, correct_query: response.data.sql_query });
      }
    } catch (err) {
      const connErrorMsg = `Erro de conexão: ${err.message}`;
      patchEntry(id, {
        status: 'done', isConnError: true, isSuccess: false,
        answer: connErrorMsg,
      });
      api.post('/chat/log', {
        question: text, answer: connErrorMsg, success: false, error: connErrorMsg,
      }).catch(() => {});
    } finally {
      setLoading(false);
      if (!isRetry) setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); }
  };

  return (
    <Box sx={{
      height: 'calc(100vh - 66px)',
      display: 'flex', flexDirection: 'column',
      bgcolor: '#f5f5f7', maxWidth: '100vw', overflow: 'hidden',
    }}>
      {/* Header */}
      <Box sx={{
        p: { xs: 1, sm: 1.5 },
        bgcolor: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(0,0,0,0.1)',
        display: 'flex', alignItems: 'center', gap: 1.5,
        minHeight: { xs: 56, sm: 64 },
      }}>
        <Avatar src="/images/avatar.png" sx={{ width: { xs: 34, sm: 40 }, height: { xs: 34, sm: 40 }, border: '2px solid rgba(0,0,0,0.1)' }} />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, fontWeight: 600, color: '#1d1d1f', lineHeight: 1.2 }}>
            Mini Bot
          </Typography>
          <Typography variant="caption" sx={{ color: '#86868b', fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
            {loading ? 'Consultando o banco...' : 'Pronto para responder'}
          </Typography>
        </Box>
      </Box>

      {/* Barra de pergunta — sempre no topo, como uma busca, não um campo de chat */}
      <Box sx={{
        p: { xs: 1.5, sm: 2 },
        bgcolor: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(0,0,0,0.1)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
          <TextField
            fullWidth
            placeholder="Pergunte algo sobre vendas, funcionárias, hotéis..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKey}
            disabled={loading}
            multiline
            maxRows={4}
            inputRef={inputRef}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: '#f1f1f3',
                fontSize: { xs: '0.9rem', sm: '1rem' },
                '&.Mui-focused': { backgroundColor: '#fff', border: '1px solid #007AFF', boxShadow: '0 0 0 3px rgba(0,122,255,0.1)' },
                '& fieldset': { border: 'none' },
              },
              '& .MuiInputBase-input': {
                color: '#1d1d1f',
                '&::placeholder': { color: '#86868b', opacity: 1 },
              },
            }}
          />
          <Button
            variant="contained"
            onClick={() => ask()}
            disabled={!input.trim() || loading}
            startIcon={loading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <Send size={16} />}
            sx={{
              flexShrink: 0, height: { xs: 36, sm: 40 }, borderRadius: 3, px: 2,
              textTransform: 'none', fontWeight: 600,
              background: loading || !input.trim()
                ? '#c7c7cc'
                : 'linear-gradient(135deg, #007AFF 0%, #5856D6 100%)',
              boxShadow: loading || !input.trim() ? 'none' : '0 4px 16px rgba(0,122,255,0.3)',
              '&:disabled': { background: '#c7c7cc', color: '#fff' },
            }}
          >
            Perguntar
          </Button>
        </Box>
        <Typography sx={{ fontSize: '0.72rem', color: '#86868b', mt: 1 }}>
          Cada pergunta é respondida sozinha — o Mini Bot não guarda o que você perguntou antes.
        </Typography>
      </Box>

      {/* Perguntas e respostas */}
      <Box sx={{
        flexGrow: 1, overflowY: 'auto', p: { xs: 1, sm: 2 },
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.2)', borderRadius: 3 },
      }}>
        {loadingHistory ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
            <CircularProgress size={22} sx={{ color: '#007AFF' }} />
          </Box>
        ) : history.length === 0 ? (
          <Box sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', height: '100%', gap: 2, textAlign: 'center', px: 2,
          }}>
            <Box sx={{
              width: 80, height: 80, borderRadius: '50%',
              background: 'linear-gradient(135deg, #007AFF 0%, #5856D6 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 8px 32px rgba(0,122,255,0.3)',
            }}>
              <Avatar src="/images/avatar.png" sx={{ width: 74, height: 74 }} />
            </Box>
            <Typography variant="h6" sx={{ color: '#1d1d1f', fontWeight: 600, fontSize: '2rem' }}>
              Mini Bot
            </Typography>
            <Typography variant="body2" sx={{ color: '#86868b', maxWidth: 340 }}>
              Pergunte sobre vendas, funcionárias, hotéis ou qualquer dado da empresa e receba uma resposta na hora.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mt: 1 }}>
              {SUGGESTIONS.map((s) => (
                <Chip
                  key={s}
                  label={s}
                  onClick={() => ask(s)}
                  disabled={loading}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: 'rgba(0,122,255,0.08)',
                    color: '#007AFF',
                    fontWeight: 500,
                    fontSize: '0.8rem',
                    border: '1px solid rgba(0,122,255,0.2)',
                    '&:hover': { bgcolor: 'rgba(0,122,255,0.15)' },
                  }}
                />
              ))}
            </Box>
          </Box>
        ) : (
          <Box sx={{
            bgcolor: history.some(e => !openIds.has(e.id)) ? 'rgba(255,255,255,0.95)' : 'transparent',
            borderRadius: 3,
            border: history.some(e => !openIds.has(e.id)) ? '1px solid rgba(0,0,0,0.08)' : 'none',
            overflow: 'hidden',
          }}>
            {history.map((entry) => (
              <QAEntry
                key={entry.id}
                entry={entry}
                open={openIds.has(entry.id)}
                onToggle={() => toggleOpen(entry.id)}
                expandedSQL={!!expandedSQL[entry.id]}
                expandedErr={!!expandedErr[entry.id]}
                onToggleSQL={toggleSQL}
                onToggleErr={toggleErr}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MiniBotPage;
